import { api } from "../api";

// ── Enums (mirror Prisma) ─────────────────────────────────────

export type BinderyRequestStatus =
  | "DRAFT"
  | "SENT"
  | "IN_REPAIR"
  | "RETURNED"
  | "CLOSED"
  | "CANCELLED";

export type BinderyItemStatus =
  | "FLAGGED"
  | "AT_BINDERY"
  | "QC_PENDING"
  | "READY_TO_SHELVE"
  | "RETURNED_UNREPAIRED"
  | "COMPLETED"
  | "CANCELLED";

export type BinderyDamageType =
  | "SPINE_BROKEN"
  | "COVER_DAMAGED"
  | "PAGES_LOOSE"
  | "PAGES_TORN"
  | "WATER_DAMAGE"
  | "MOLD"
  | "AGING"
  | "OTHER";

export type BinderyRepairType =
  | "REBIND"
  | "RECASE"
  | "SPINE_REPAIR"
  | "COVER_REPAIR"
  | "PAGE_REPAIR"
  | "CLEANING"
  | "OTHER";

export type ThesisDegreeLevel = "UG_FINAL" | "MASTERS" | "PHD";
export type ThesisBindingType = "SOFT" | "HARD" | "GOLD_EMBOSS";
export type ThesisPrintStatus =
  | "SUBMITTED"
  | "FORMAT_CHECK"
  | "APPROVED"
  | "PRINTING"
  | "BINDING"
  | "READY"
  | "COLLECTED"
  | "REJECTED"
  | "CANCELLED";

// ── Types ────────────────────────────────────────────────────

export interface BinderyVendor {
  id: string;
  tenantId: string;
  name: string;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  defaultTurnaroundDays: number;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { requests: number };
}

export interface BinderyVendorInput {
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  defaultTurnaroundDays?: number;
  notes?: string;
  isActive?: boolean;
}

export interface BinderyVendorDetail extends BinderyVendor {
  requests: {
    id: string;
    requestNumber: string;
    status: BinderyRequestStatus;
    sentAt: string | null;
    expectedReturnAt: string | null;
    costTotal: number;
  }[];
}

export interface BinderyRequestItem {
  id: string;
  requestId: string;
  itemId: string;
  status: BinderyItemStatus;
  damageType?: BinderyDamageType | null;
  repairType?: BinderyRepairType | null;
  instructions?: string | null;
  cost: number;
  preBindingConditionCode?: string | null;
  postBindingConditionCode?: string | null;
  item: {
    id: string;
    barcode: string;
    accessionNumber: number;
    statusCode: string;
    conditionCode?: string | null;
    biblio: { id: string; title: string; authors: string[] };
  };
  history: BinderyStatusHistory[];
}

export interface BinderyStatusHistory {
  id: string;
  requestItemId: string;
  fromStatus?: BinderyItemStatus | null;
  toStatus: BinderyItemStatus;
  note?: string | null;
  createdAt: string;
  changedBy: { id: string; firstName?: string | null; lastName?: string | null };
}

export interface BinderyRequestSummary {
  id: string;
  tenantId: string;
  requestNumber: string;
  status: BinderyRequestStatus;
  vendorId: string;
  branchId?: string | null;
  sentAt?: string | null;
  expectedReturnAt?: string | null;
  receivedAt?: string | null;
  shippingReference?: string | null;
  costTotal: number;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  vendor: { id: string; name: string };
  branch?: { id: string; name: string; code: string } | null;
  _count: { items: number };
}

export interface BinderyRequestDetail extends Omit<BinderyRequestSummary, "_count"> {
  vendor: { id: string; name: string; contactPerson?: string | null; email?: string | null };
  createdBy: { id: string; firstName?: string | null; lastName?: string | null; email: string };
  items: BinderyRequestItem[];
}

export interface BinderyDashboardStats {
  totals: {
    openRequests: number;
    itemsAtBindery: number;
    itemsAwaitingQc: number;
    overdueBatches: number;
    batchesSentThisMonth: number;
  };
  topVendors: {
    vendorId: string;
    vendorName: string;
    batches: number;
    totalSpent: number;
  }[];
  recentActivity: {
    id: string;
    toStatus: BinderyItemStatus;
    fromStatus?: BinderyItemStatus | null;
    note?: string | null;
    createdAt: string;
    requestItem: {
      id: string;
      item: { barcode: string };
      request: { id: string; requestNumber: string };
    };
    changedBy: { firstName?: string | null; lastName?: string | null };
  }[];
}

export type ThesisPaymentMethod =
  | "CASH"
  | "BANK_TRANSFER"
  | "POS"
  | "PAYSTACK"
  | "FLUTTERWAVE"
  | "OTHER";

export type OfflineThesisPaymentMethod = "CASH" | "BANK_TRANSFER" | "POS" | "OTHER";

export interface ThesisLinkedInvoice {
  id: string;
  status: "pending" | "paid" | "cancelled" | string;
  amountDue: number | string;
  amountPaid: number | string | null;
  currency: string;
  paidAt: string | null;
}

export interface ThesisPrintRequest {
  id: string;
  tenantId: string;
  patronId: string;
  requestNumber: string;
  degreeLevel: ThesisDegreeLevel;
  title: string;
  supervisor?: string | null;
  departmentId?: string | null;
  pageCount: number;
  copies: number;
  bindingType: ThesisBindingType;
  colorPages: number;
  paperGrade?: string | null;
  fileUrl?: string | null;
  fileKey?: string | null;
  formattingNotes?: string | null;
  cost: number;
  status: ThesisPrintStatus;
  rejectionReason?: string | null;
  reviewedById?: string | null;
  reviewedAt?: string | null;
  paidAt?: string | null;
  paymentMethod?: ThesisPaymentMethod | null;
  paymentReference?: string | null;
  paymentNote?: string | null;
  recordedPaymentById?: string | null;
  invoiceId?: string | null;
  invoice?: ThesisLinkedInvoice | null;
  readyAt?: string | null;
  collectedAt?: string | null;
  submittedAt: string;
  updatedAt: string;
  patron: { id: string; firstName?: string | null; lastName?: string | null; email: string };
  department?: { id: string; name: string } | null;
  reviewedBy?: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

export interface ThesisPriceRule {
  id: string;
  tenantId: string;
  degreeLevel: ThesisDegreeLevel;
  bindingType: ThesisBindingType;
  basePrice: number;
  perPagePrice: number;
  perCopyPrice: number;
  colorPagePrice: number;
  effectiveFrom: string;
  isActive: boolean;
}

export interface Paginated<T, Key extends string> {
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export type BinderyVendorListResponse = Paginated<BinderyVendor, "vendors"> & {
  vendors: BinderyVendor[];
};
export type BinderyRequestListResponse = Paginated<BinderyRequestSummary, "requests"> & {
  requests: BinderyRequestSummary[];
};
export type ThesisListResponse = Paginated<ThesisPrintRequest, "requests"> & {
  requests: ThesisPrintRequest[];
};

// ── API ──────────────────────────────────────────────────────

export const binderyApi = {
  dashboard: {
    stats: () => api.get<BinderyDashboardStats>("/bindery/dashboard/stats").then((r) => r.data),
  },

  vendors: {
    list: (params?: {
      search?: string;
      isActive?: boolean;
      limit?: number;
      cursor?: string;
      sortBy?: "name" | "createdAt";
      sortOrder?: "asc" | "desc";
    }) => api.get<BinderyVendorListResponse>("/bindery/vendors", { params }).then((r) => r.data),

    getById: (id: string) =>
      api.get<BinderyVendorDetail>(`/bindery/vendors/${id}`).then((r) => r.data),

    create: (data: BinderyVendorInput) =>
      api.post<BinderyVendor>("/bindery/vendors", data).then((r) => r.data),

    update: (id: string, data: Partial<BinderyVendorInput>) =>
      api.patch<BinderyVendor>(`/bindery/vendors/${id}`, data).then((r) => r.data),
  },

  requests: {
    list: (params?: {
      search?: string;
      status?: BinderyRequestStatus;
      vendorId?: string;
      branchId?: string;
      overdue?: "true";
      limit?: number;
      cursor?: string;
      sortBy?: "createdAt" | "sentAt" | "expectedReturnAt";
      sortOrder?: "asc" | "desc";
    }) => api.get<BinderyRequestListResponse>("/bindery/requests", { params }).then((r) => r.data),

    getById: (id: string) =>
      api.get<BinderyRequestDetail>(`/bindery/requests/${id}`).then((r) => r.data),

    create: (data: {
      vendorId: string;
      branchId?: string;
      notes?: string;
      shippingReference?: string;
      items: {
        itemId: string;
        damageType?: BinderyDamageType;
        repairType?: BinderyRepairType;
        instructions?: string;
        cost?: number;
        preBindingConditionCode?: string;
      }[];
    }) => api.post<BinderyRequestDetail>("/bindery/requests", data).then((r) => r.data),

    update: (
      id: string,
      data: { notes?: string; shippingReference?: string; expectedReturnAt?: string }
    ) => api.patch<BinderyRequestDetail>(`/bindery/requests/${id}`, data).then((r) => r.data),

    send: (
      id: string,
      data: { expectedReturnAt?: string; shippingReference?: string; note?: string }
    ) => api.post<BinderyRequestDetail>(`/bindery/requests/${id}/send`, data).then((r) => r.data),

    markInRepair: (id: string) =>
      api.post<BinderyRequestDetail>(`/bindery/requests/${id}/in-repair`).then((r) => r.data),

    receiveBatch: (id: string) =>
      api.post<BinderyRequestDetail>(`/bindery/requests/${id}/receive`).then((r) => r.data),

    qcItem: (
      id: string,
      itemRowId: string,
      data: {
        qcOutcome: "READY_TO_SHELVE" | "RETURNED_UNREPAIRED";
        postBindingConditionCode?: string;
        cost?: number;
        note?: string;
      }
    ) =>
      api
        .post<BinderyRequestDetail>(`/bindery/requests/${id}/items/${itemRowId}/qc`, data)
        .then((r) => r.data),

    cancel: (id: string) =>
      api.post<BinderyRequestDetail>(`/bindery/requests/${id}/cancel`).then((r) => r.data),
  },

  items: {
    lookup: (barcode: string) =>
      api
        .get<{
          item: {
            id: string;
            barcode: string;
            accessionNumber: number;
            statusCode: string;
            conditionCode?: string | null;
            branchId?: string | null;
            biblio: { id: string; title: string; authors: string[]; isbn?: string | null };
          };
          openBindery: {
            id: string;
            status: BinderyItemStatus;
            request: { id: string; requestNumber: string };
          } | null;
        }>("/bindery/items/lookup", { params: { barcode } })
        .then((r) => r.data),
  },

  history: {
    forItem: (itemId: string) =>
      api.get<BinderyRequestItem[]>(`/bindery/history/items/${itemId}`).then((r) => r.data),

    repairTypes: (from?: string) =>
      api
        .get<
          { repairType: BinderyRepairType; count: number }[]
        >("/bindery/history/reports/repair-types", { params: from ? { from } : undefined })
        .then((r) => r.data),

    damageTypes: (from?: string) =>
      api
        .get<
          { damageType: BinderyDamageType; count: number }[]
        >("/bindery/history/reports/damage-types", { params: from ? { from } : undefined })
        .then((r) => r.data),

    vendorPerformance: () =>
      api
        .get<
          {
            vendorId: string;
            vendorName: string;
            defaultTurnaroundDays: number;
            averageTurnaroundDays: number;
            batchesCompleted: number;
            totalSpent: number;
          }[]
        >("/bindery/history/reports/vendor-performance")
        .then((r) => r.data),
  },

  thesis: {
    estimate: (data: {
      degreeLevel: ThesisDegreeLevel;
      bindingType: ThesisBindingType;
      pageCount: number;
      copies: number;
      colorPages: number;
    }) =>
      api
        .post<{
          unitPrice: number;
          totalPrice: number;
          rule: ThesisPriceRule | null;
        }>("/bindery/thesis/estimate", data)
        .then((r) => r.data),

    submit: (data: {
      degreeLevel: ThesisDegreeLevel;
      title: string;
      supervisor: string;
      departmentId?: string;
      pageCount: number;
      copies?: number;
      bindingType: ThesisBindingType;
      colorPages?: number;
      paperGrade?: string;
      fileUrl: string;
      fileKey?: string;
      formattingNotes?: string;
    }) => api.post<ThesisPrintRequest>("/bindery/thesis", data).then((r) => r.data),

    mine: (params?: { limit?: number; cursor?: string; status?: ThesisPrintStatus }) =>
      api.get<ThesisListResponse>("/bindery/thesis/mine", { params }).then((r) => r.data),

    mineById: (id: string) =>
      api.get<ThesisPrintRequest>(`/bindery/thesis/mine/${id}`).then((r) => r.data),

    updateMine: (
      id: string,
      data: {
        degreeLevel: ThesisDegreeLevel;
        title: string;
        supervisor: string;
        departmentId?: string;
        pageCount: number;
        copies?: number;
        bindingType: ThesisBindingType;
        colorPages?: number;
        paperGrade?: string;
        fileUrl: string;
        fileKey?: string;
        formattingNotes?: string;
      }
    ) => api.patch<ThesisPrintRequest>(`/bindery/thesis/mine/${id}`, data).then((r) => r.data),

    cancelMine: (id: string) =>
      api.post<ThesisPrintRequest>(`/bindery/thesis/mine/${id}/cancel`).then((r) => r.data),

    list: (params?: {
      search?: string;
      status?: ThesisPrintStatus;
      degreeLevel?: ThesisDegreeLevel;
      departmentId?: string;
      limit?: number;
      cursor?: string;
    }) => api.get<ThesisListResponse>("/bindery/thesis", { params }).then((r) => r.data),

    getById: (id: string) =>
      api.get<ThesisPrintRequest>(`/bindery/thesis/${id}`).then((r) => r.data),

    review: (
      id: string,
      data: { status: ThesisPrintStatus; rejectionReason?: string; cost?: number }
    ) => api.patch<ThesisPrintRequest>(`/bindery/thesis/${id}/review`, data).then((r) => r.data),

    markPaid: (
      id: string,
      data: { method: OfflineThesisPaymentMethod; reference?: string; note?: string }
    ) => api.post<ThesisPrintRequest>(`/bindery/thesis/${id}/mark-paid`, data).then((r) => r.data),

    listPriceRules: () =>
      api.get<ThesisPriceRule[]>("/bindery/thesis/settings/price-rules").then((r) => r.data),

    upsertPriceRule: (data: {
      degreeLevel: ThesisDegreeLevel;
      bindingType: ThesisBindingType;
      basePrice: number;
      perPagePrice?: number;
      perCopyPrice?: number;
      colorPagePrice?: number;
      isActive?: boolean;
    }) =>
      api.post<ThesisPriceRule>("/bindery/thesis/settings/price-rules", data).then((r) => r.data),
  },
};
